import { Button, Box, Flex } from '@mantine/core';
import { BetaIcon, BetaText } from '../../../../../lib/components';
import classes from './Filters.module.css';
import useValidatedSearchParams, {
  SearchParamKey,
} from '../../../../router/useValidatedSearchParams';
import { Group, Pill, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import SwitchFilter from './SwitchFilter';
import RadioFilter from './RadioFilter';
import SelectFilter from './SelectFilter';
import SliderFilter from './SliderFilter';
import DateFilter from './DateFilter';

export type Filter<T> = {
  key: SearchParamKey;
  label: string;
  match: (record: T, value: string) => boolean;
  type: 'switch' | 'radio' | 'select' | 'slider' | 'date';
  options?: { label: string; value: string }[];
  range?: {
    min: number | string | undefined | Date;
    max: number | string | undefined | Date;
  };
  suffix?: string;
  minLabel?: string;
  maxLabel?: string;
};

export type FiltersProps<T> = {
  filters: Filter<T>[];
};

const Filters = <T,>({ filters }: FiltersProps<T>) => {
  const {
    validatedParams,
    SEARCH_PARAMS_KEYS: { FILTERS_PANEL },
    setValidatedParam,
  } = useValidatedSearchParams();
  const { t } = useTranslation();

  const expandedFilters = validatedParams[FILTERS_PANEL] === 'true';

  const hasActiveFilters = filters?.some(({ key }) => validatedParams[key]);

  const toggleCollapseFilters = () => {
    setValidatedParam(FILTERS_PANEL, expandedFilters ? null : 'true');
  };

  const clearFilters = () => {
    filters.forEach(({ key }) => {
      setValidatedParam(key, null);
    });
  };

  const activeFilters = filters
    .filter(({ key }) => validatedParams[key])
    .map(({ key, options, suffix, range }) => {
      const value = validatedParams[key] as string;
      const separator = range ? ' - ' : ', ';

      const labelValue = value
        .split(',')
        .map((v) => options?.find(({ value }) => value === v)?.label ?? v)
        .join(separator);

      return {
        label: [labelValue, suffix].join(' '),
        value: key,
      };
    });

  return (
    <>
      <Box className={classes.filtersButtonWrapper}>
        {hasActiveFilters && <div className={classes.filterButtonIndicator} />}
        <Button
          key={hasActiveFilters.toString()}
          className={classes.filtersButton}
          onClick={toggleCollapseFilters}
          bg={expandedFilters ? 'dark.7' : 'darkTr.1'}
          radius="sm"
          size="sm"
          p={9}
        >
          <BetaIcon icon="filter" size="sm" />
        </Button>
        <Flex gap={8}>
          {activeFilters.map((filter) => (
            <Pill
              key={filter.value}
              onRemove={() => setValidatedParam(filter.value, null)}
              withRemoveButton
              size="md"
              radius="2px"
              classNames={{
                root: classes.activeFilters,
              }}
            >
              <Text size="md" truncate maw={250} title={filter.label}>
                {filter.label}
              </Text>
            </Pill>
          ))}
        </Flex>
      </Box>
      {expandedFilters && (
        <Box className={classes.filtersPanel}>
          <Flex justify="space-between" align="center">
            <BetaText variant="h2">Filters</BetaText>
            <Group>
              <Button size="md" variant="transparent" onClick={clearFilters}>
                {t('plots.filters.clear')}
              </Button>
              <Button
                size="md"
                variant="transparent"
                onClick={toggleCollapseFilters}
                p={0}
              >
                <BetaIcon icon="close" size="md" />
              </Button>
            </Group>
          </Flex>
          <Box className={classes.filtersFields}>
            {filters.map((filter) => {
              const FilterInput = {
                switch: SwitchFilter,
                radio: RadioFilter,
                select: SelectFilter,
                slider: SliderFilter,
                date: DateFilter,
              }[filter.type];

              return <FilterInput key={filter.key} filter={filter} />;
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Filters;
export { default as useFilters } from './useFilters';
